import { DataType, Form, FormOptions, Section, SelectOption } from '@globaleris/ui.library'
import { Box, CardHeader, makeStyles, Paper, useTheme } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumUnidadMedida } from '../../../../api'
import {
  ConceptQuantityTypeOptionsModel,
  NewConceptQuantity
} from '../../../../api/Argentia/endpoints/concepts/model/concepts'
import { CardWithTransition } from '../../../../components/shared/cardWithTransition/CardWithTransition'
import { LoadingCard } from '../../../../components/shared/loadingCard/loadingCard'
import { enumTranslateOption, generateUniqueKeyWithNoPrefix } from '../../../../helpers'
import { getCustomerDomain } from '../../../../redux'
import { conceptQuantityCardStyles } from './conceptQuantityCard.styles'

interface ConceptQuantityProps {
  quantityType?: ConceptQuantityTypeOptionsModel[] | null
  conceptQuantity?: NewConceptQuantity | null
  subtitle?: string
  onChangeInput?: (conceptQuantity: NewConceptQuantity) => void
  onAdd?: () => void
}

export const ConceptQuantityCard: React.FC<ConceptQuantityProps> = ({
  conceptQuantity,
  onChangeInput,
  onAdd,
  subtitle,
  quantityType
  // children
}) => {
  const [t] = useTranslation(['conceptsEdit', 'globalerisLibrary', 'enums'])
  const currentTheme = useTheme()
  const useStyles = makeStyles(conceptQuantityCardStyles(currentTheme))
  const classes = useStyles()

  const [isFetchingMeasureOptions, setIsFetchingMeasureOptions] = useState<boolean>(false)
  const [measureOptions, setMeasureOptions] = useState<SelectOption[]>([])
  const [quantityTypeOptions, setQuantityTypeOptions] = useState<SelectOption[]>([])

  //Form config Starts
  const options: FormOptions = {
    columns: 3,
    elevation: 0,
    analytics: {
      customer: getCustomerDomain(),
      view: t('concepts:components.conceptQuantityCard.analytics')
    }
  }

  const onChange = (input: 'measureUnit' | 'data', value) => {
    const tempId = generateUniqueKeyWithNoPrefix()
    onChangeInput &&
      onChangeInput({
        id: conceptQuantity?.id,
        tempId: !conceptQuantity?.id
          ? conceptQuantity?.tempId
            ? conceptQuantity?.tempId
            : tempId
          : undefined,
        [input]: value
      })
  }

  const limitFormSections: Section[] = [
    {
      inputs: [
        {
          name: 'data',
          label: t('conceptsEdit:components.conceptQuantityCard.inputs.quantity'),
          type: DataType.SELECT,
          selectOptions: quantityTypeOptions,
          required: true,
          onChange: (value, formState) => {
            onChange('data', value)
          }
        },
        {
          name: 'measureUnit',
          label: t('conceptsEdit:components.conceptQuantityCard.inputs.measureUnit'),
          type: DataType.SELECT,
          isLoading: isFetchingMeasureOptions,
          selectOptions: measureOptions,
          required: true,
          readOnly: !!conceptQuantity?.measureUnit,
          onChange: (value, formState) => {
            onChange('measureUnit', value)
          }
        }
      ]
    }
  ]

  const handleAddClick = () => {
    onAdd && onAdd()
  }

  useEffect(() => {
    setQuantityTypeOptions(
      quantityType ? quantityType.map(qt => ({ label: qt.dataDescription, value: qt.dataId })) : []
    )
  }, [quantityType])

  useEffect(() => {
    const measureTypeRelatedToData = quantityType?.find(qt => qt.dataId === conceptQuantity?.data)
    setMeasureOptions(
      quantityType
        ? quantityType
            .filter(qt => qt.dataId === conceptQuantity?.data)
            .map(qt => ({
              label: enumTranslateOption(qt.measureUnitId, EnumUnidadMedida, 'enums:UnitMeasure'),
              value: qt.measureUnitId
            }))
        : []
    )
    measureTypeRelatedToData && onChange('measureUnit', measureTypeRelatedToData.measureUnitId)
  }, [conceptQuantity?.data, quantityType])

  if (!conceptQuantity) {
    return <LoadingCard />
  }

  return (
    <>
      <Box marginTop={2} marginBottom={2}>
        <CardWithTransition show={true} raised className={classes.card}>
          <Paper elevation={3}>
            <CardHeader
              title={t('conceptsEdit:components.conceptQuantityCard.titles.cardTitle')}
              titleTypographyProps={{ variant: 'h2', color: 'secondary' }}
            />
            <Form sections={limitFormSections} options={options} data={conceptQuantity} />
          </Paper>
        </CardWithTransition>
      </Box>
    </>
  )
}
